import { Edit, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { EditPromptVariableForm } from './UpdateVariable';

import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableCellWithTooltip,
} from '@/components/ui/Table';
import { useGetPromptVariables } from '@/hooks/useGetPromptVariables';
import { PromptVariable } from '@/services/PromptsService';

export function VariablesTable({ name }: { name: string }) {
  const endVariablesRef = useRef<HTMLDivElement>(null);

  const [showEditVariable, setShowEditVariable] = useState(false);
  const [selectedVariable, setSelectedVariable] =
    useState<PromptVariable | null>(null);

  const {
    promptVariables,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetPromptVariables({
    name: name ? [name] : undefined,
    limit: 10,
  });

  useEffect(() => {
    if (!endVariablesRef.current) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const { isIntersecting } = entries[0];

        if (!hasNextPage) {
          obs.disconnect();
          return;
        }

        if (isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '30px',
      },
    );

    observer.observe(endVariablesRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  function handleSuccess() {
    setShowEditVariable(false);
    setSelectedVariable(null);
  }

  function handleEditVariable(variable: PromptVariable) {
    setSelectedVariable(variable);
    setShowEditVariable(true);
  }

  function handleCloseEdit() {
    setShowEditVariable(false);
    setSelectedVariable(null);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Query</TableHead>
            <TableHead>Updated At</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {promptVariables.length === 0 && !isFetching && (
            <TableRow>
              <TableCell
                colSpan={4}
                className="py-10 text-center text-gray-500"
              >
                No data available
              </TableCell>
            </TableRow>
          )}
          {promptVariables.map((variable, index) => (
            <TableRow
              key={`${variable.id}-${index}`}
              className="hover:bg-gray-100"
            >
              <TableCellWithTooltip className="font-medium">
                {variable.name}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="max-w-md">
                {variable.query}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="text-md text-gray-600">
                {new Date(variable.updatedAt || '').toLocaleDateString(
                  'pt-BR',
                  {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  },
                )}
              </TableCellWithTooltip>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Button
                    onClick={() => handleEditVariable(variable)}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 hover:bg-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div ref={endVariablesRef} />

      {(isFetching || isFetchingNextPage) && (
        <div className="flex justify-center py-4">
          <LoaderCircle className="h-6 w-6 animate-spin" />
        </div>
      )}

      {selectedVariable && (
        <EditPromptVariableForm
          open={showEditVariable}
          onClose={handleCloseEdit}
          promptVariable={selectedVariable}
          onSuccess={handleSuccess}
          filter={{ name: name ? [name] : undefined }}
        />
      )}
    </div>
  );
}
